import React from 'react';
import { DAY_NAMES, rowStyles, tableHeadings } from './constants';
import { SquarePen, Trash2 } from 'lucide-react';
import Button from '../../../../components/ComponentsV1/Button';

const formatTime = (hour, min) => {
  const safeHour = Number(hour) || 0;
  const safeMin = Number(min) || 0;
  const h = safeHour % 12 === 0 ? 12 : safeHour % 12;
  const m = String(safeMin).padStart(2, '0');
  const period = safeHour < 12 ? 'AM' : 'PM';
  return `${h}:${m} ${period}`;
};

const BusinessHoursCell = ({ businessHours }) => {
  if (!Array.isArray(businessHours) || businessHours.length === 0) {
    return <span className='tw-text-grey-400'>-</span>;
  }
  const sorted = [...businessHours].sort((a, b) => (a?.day_of_week ?? 0) - (b?.day_of_week ?? 0));
  return (
    <div className='tw-flex tw-flex-col tw-gap-3'>
      {sorted.map((bh) => (
        <span key={`${bh?.day_of_week}-${bh?.start_hour}-${bh?.start_min}-${bh?.end_hour}-${bh?.end_min}`} className='tw-whitespace-nowrap tw-text-sm tw-flex tw-items-center tw-gap-6'>
          <span className='tw-font-medium tw-text-grey-700 tw-inline-block tw-w-10 tw-shrink-0'>
            {DAY_NAMES[bh?.day_of_week] ?? '-'}
          </span>
          <span className='tw-text-grey-500'>
            {formatTime(bh?.start_hour ?? 0, bh?.start_min ?? 0)}
            {' \u2013 '}
            {formatTime(bh?.end_hour ?? 0, bh?.end_min ?? 0)}
          </span>
        </span>
      ))}
    </div>
  );
};

const OBHQTable = ({ queueData, onUpdate, onDelete }) => {
  if (!Array.isArray(queueData)) {
    return null;
  }
  
  return (
    <div className='tw-flex tw-items-center tw-w-full tw-rounded-2xl tw-bg-grey-100/70 tw-border tw-border-1 tw-border-solid tw-border-grey-200 tw-overflow-x-auto tw-mt-2'>
      <div className='tw-text-lg tw-font-medium tw-w-full tw-flex tw-flex-col tw-leading-none tw-gap-4'>
        <table className='tw-rounded-xl tw-border-none tw-table-fixed'>
          <thead className='tw-rounded-xl'>
            <tr className='tw-rounded-2xl'>
              {Array.isArray(tableHeadings) && tableHeadings.map((heading, index) => (
                <th
                  key={heading?.key}
                  title={heading?.name}
                  className={`tw-p-3 ${heading?.width} tw-bg-blue-200 tw-text-grey-800 ${
                    index === tableHeadings.length - 1
                      ? 'tw-border-r-0'
                      : 'tw-border-r'
                  } tw-border-solid tw-border-y-0 tw-border-l-0 tw-border-grey-300 tw-overflow-hidden tw-text-ellipsis tw-leading-snug tw-text-center`}
                >
                  <span className='tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center tw-my-3'>
                    {heading?.name}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className='tw-bg-white'>
              {Array.isArray(queueData) && queueData.map((item, index) => (
                <tr
                  key={item?.queue_name}
                  className={
                    index === queueData.length - 1
                      ? 'tw-rounded-b-xl tw-border-none tw-shadow-none tw-text-grey-800 tw-text-center'
                      : 'tw-text-grey-800 tw-text-center tw-shadow-none tw-border tw-border-solid tw-border-grey-200 !tw-border-l-0 !tw-border-r-0'
                  }
                >
                  <td
                    title={item?.queue_name}
                    className={`tw-px-4 tw-border-r tw-py-4 tw-border-solid tw-overflow-hidden tw-text-ellipsis tw-border-y-0 tw-border-l-0 tw-border-grey-200 tw-whitespace-nowrap tw-transition-all tw-ease-in-out tw-duration-200 tw-relative tw-group ${
                      index === queueData.length - 1 ? 'tw-rounded-bl-xl' : ''
                    }`}
                  >
                    {item?.queue_name}
                  </td>
                  <td className={rowStyles}>{item?.timezone ?? '-'}</td>
                  <td className={`${rowStyles} tw-whitespace-normal tw-break-words tw-text-left`}>
                    {item?.canned_response || '-'}
                  </td>
                  <td
                    className={'tw-px-3 tw-py-4 tw-border-r tw-border-solid tw-border-y-0 tw-border-l-0 tw-border-grey-200 tw-text-left tw-align-top'}
                  >
                    <BusinessHoursCell businessHours={item?.business_hours} />
                  </td>
                  <td
                    className={
                      index === queueData.length - 1
                        ? 'tw-rounded-br-2xl tw-border-none tw-px-3 tw-py-4 tw-align-top'
                        : 'tw-px-3 tw-py-4 tw-align-top'
                    }
                  >
                    <div className='tw-flex tw-flex-row tw-gap-2 tw-items-center'>
                      <Button
                        type='primary'
                        size='small'
                        onClick={() => onUpdate?.(item)}
                        title={
                          <div className='tw-flex tw-items-center tw-gap-1 tw-whitespace-nowrap'>
                            <SquarePen size={13} />
                            Update
                          </div>
                        }
                      />
                      <button
                        onClick={() => onDelete?.(item?.queue_name)}
                        className='tw-flex tw-items-center tw-gap-1 tw-text-red-500 tw-bg-white tw-border tw-border-solid tw-border-red-300 tw-rounded-xl tw-px-3 tw-py-2 tw-cursor-pointer tw-text-sm tw-font-medium hover:tw-bg-red-50 tw-transition-all tw-duration-200 tw-ease-in-out tw-whitespace-nowrap'
                      >
                        <Trash2 size={13} />
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
        </table>
      </div>
    </div>
  );
};

export default OBHQTable;
